/**
 * useAudioEngine.ts
 * =============================================================================
 * Custom React hook for interacting with the AudioEngine.
 * Provides a clean interface for components to control audio playback.
 * 
 * @author Neuro-Audio Lab
 * @version 1.0.0
 */

import { useEffect, useCallback, useRef } from 'react';
import { usePlayerStore } from '../store/playerStore';
import { getAudioEngine } from '../core/AudioEngine';
import type { AudioMode, TrackId } from '../core/AudioEngine';

// =============================================================================
// HOOK RETURN TYPE
// =============================================================================

export interface UseAudioEngineReturn {
  // State
  isInitialized: boolean;
  isPlaying: boolean;
  isPaused: boolean;
  isLoading: boolean;
  error: string | null;
  currentMode: AudioMode | null;

  // Volumes
  volumes: {
    ambience: number;
    tone: number;
    voice: number;
  };
  masterVolume: number;
  isMuted: boolean;

  // Actions
  initialize: () => Promise<void>;
  play: (mode?: AudioMode) => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  toggle: () => void;
  setMode: (mode: AudioMode) => void;
  setVolume: (trackId: TrackId, volume: number) => void;
  setMasterVolume: (volume: number) => void;
  toggleMute: () => void;
  loadAmbience: (url: string) => Promise<void>;
  clearAmbience: () => void;                        // [v3.0] Fix zombie ambience
  loadVoice: (url: string) => Promise<void>;
  loadEntrainment: (url: string) => Promise<void>; // [v2.8]
  clearEntrainment: () => void;                     // [v2.8]
  loadTrigger: (url: string) => Promise<void>;      // [v2.9]
  clearTrigger: () => void;                         // [v2.9]
  clearError: () => void;
}

// =============================================================================
// HOOK IMPLEMENTATION
// =============================================================================

/**
 * Custom hook for audio engine control
 * 
 * Usage:
 * ```tsx
 * function Player() {
 *   const { 
 *     isPlaying, 
 *     play, 
 *     pause, 
 *     setVolume 
 *   } = useAudioEngine();
 *   
 *   return (
 *     <button onClick={() => isPlaying ? pause() : play('focus')}>
 *       {isPlaying ? 'Pause' : 'Play'}
 *     </button>
 *   );
 * }
 * ```
 */
export function useAudioEngine(): UseAudioEngineReturn {
  // Get state and actions from store
  const isInitialized = usePlayerStore((s) => s.isInitialized);
  const isPlaying = usePlayerStore((s) => s.isPlaying);
  const isPaused = usePlayerStore((s) => s.isPaused);
  const isLoading = usePlayerStore((s) => s.isLoading);
  const error = usePlayerStore((s) => s.error);
  const currentMode = usePlayerStore((s) => s.currentMode);
  const volumes = usePlayerStore((s) => s.volumes);
  const masterVolume = usePlayerStore((s) => s.masterVolume);
  const isMuted = usePlayerStore((s) => s.isMuted);
  
  // Actions
  const initializeEngine = usePlayerStore((s) => s.initializeEngine);
  const playAction = usePlayerStore((s) => s.play);
  const playWithModeAction = usePlayerStore((s) => s.playWithMode);
  const pauseAction = usePlayerStore((s) => s.pause);
  const resumeAction = usePlayerStore((s) => s.resume);
  const stopAction = usePlayerStore((s) => s.stop);
  const setModeAction = usePlayerStore((s) => s.setMode);
  const setTrackVolume = usePlayerStore((s) => s.setTrackVolume);
  const setMasterVolumeAction = usePlayerStore((s) => s.setMasterVolume);
  const toggleMuteAction = usePlayerStore((s) => s.toggleMute);
  const loadAmbienceAction = usePlayerStore((s) => s.loadAmbience);
  const clearAmbienceAction = usePlayerStore((s) => s.clearAmbience); // [v3.0]
  const loadVoiceAction = usePlayerStore((s) => s.loadVoice);
  const loadEntrainmentAction = usePlayerStore((s) => s.loadEntrainment); // [v2.8]
  const clearEntrainmentAction = usePlayerStore((s) => s.clearEntrainment); // [v2.8]
  const loadTriggerAction = usePlayerStore((s) => s.loadTrigger);           // [v2.9]
  const clearTriggerAction = usePlayerStore((s) => s.clearTrigger);         // [v2.9]
  const clearErrorAction = usePlayerStore((s) => s.clearError);
  
  // Memoized callbacks to prevent unnecessary re-renders
  const initialize = useCallback(async () => {
    await initializeEngine();
  }, [initializeEngine]);
  
  const play = useCallback(async (mode?: AudioMode) => {
    if (mode) {
      await playWithModeAction(mode);
    } else {
      await playAction();
    }
  }, [playAction, playWithModeAction]);
  
  const pause = useCallback(() => {
    pauseAction();
  }, [pauseAction]);
  
  const resume = useCallback(() => {
    resumeAction();
  }, [resumeAction]);
  
  const stop = useCallback(() => {
    stopAction();
  }, [stopAction]);
  
  const toggle = useCallback(() => {
    if (isPlaying && !isPaused) {
      pause();
    } else if (isPaused) {
      resume();
    } else {
      play();
    }
  }, [isPlaying, isPaused, play, pause, resume]);
  
  const setMode = useCallback((mode: AudioMode) => {
    setModeAction(mode);
  }, [setModeAction]);
  
  const setVolume = useCallback((trackId: TrackId, volume: number) => {
    setTrackVolume(trackId, volume);
  }, [setTrackVolume]);
  
  const setMasterVol = useCallback((volume: number) => {
    setMasterVolumeAction(volume);
  }, [setMasterVolumeAction]);
  
  const toggleMute = useCallback(() => {
    toggleMuteAction();
  }, [toggleMuteAction]);
  
  const loadAmbience = useCallback(async (url: string) => {
    await loadAmbienceAction(url);
  }, [loadAmbienceAction]);

  const clearAmbience = useCallback(() => { // [v3.0]
    clearAmbienceAction();
  }, [clearAmbienceAction]);

  const loadVoice = useCallback(async (url: string) => {
    await loadVoiceAction(url);
  }, [loadVoiceAction]);

  const loadEntrainment = useCallback(async (url: string) => { // [v2.8]
    await loadEntrainmentAction(url);
  }, [loadEntrainmentAction]);

  const clearEntrainment = useCallback(() => { // [v2.8]
    clearEntrainmentAction();
  }, [clearEntrainmentAction]);

  const loadTrigger = useCallback(async (url: string) => { // [v2.9]
    await loadTriggerAction(url);
  }, [loadTriggerAction]);

  const clearTrigger = useCallback(() => { // [v2.9]
    clearTriggerAction();
  }, [clearTriggerAction]);

  const clearError = useCallback(() => {
    clearErrorAction();
  }, [clearErrorAction]);

  return {
    // State
    isInitialized,
    isPlaying,
    isPaused,
    isLoading,
    error,
    currentMode,
    volumes,
    masterVolume,
    isMuted,

    // Actions
    initialize,
    play,
    pause,
    resume,
    stop,
    toggle,
    setMode,
    setVolume,
    setMasterVolume: setMasterVol,
    toggleMute,
    loadAmbience,
    clearAmbience,
    loadVoice,
    loadEntrainment,
    clearEntrainment,
    loadTrigger,
    clearTrigger,
    clearError,
  };
}

// =============================================================================
// TIMER HOOK (v3.0 - New Timer System)
// =============================================================================

/**
 * Hook for timer functionality
 * [v2.0] REFACTORED: Timer tick logic moved to MainLayout for persistence
 * This hook now only provides state and actions, no auto-tick
 */
export function useTimer() {
  // Get timer state from store
  const timerState = usePlayerStore((s) => s.timerState);
  const currentTimerProfile = usePlayerStore((s) => s.currentTimerProfile);
  const sessionElapsedTime = usePlayerStore((s) => s.sessionElapsedTime);
  const remainingTime = usePlayerStore((s) => s.remainingTime);
  const isInBreak = usePlayerStore((s) => s.isInBreak);
  const pomodoroPhase = usePlayerStore((s) => s.pomodoroPhase);

  // Get timer actions
  const setTimerState = usePlayerStore((s) => s.setTimerState);
  const setTimerProfile = usePlayerStore((s) => s.setTimerProfile);
  const resetSession = usePlayerStore((s) => s.resetSession);
  const toggleBreak = usePlayerStore((s) => s.toggleBreak);

  // [v2.0] REMOVED: Auto-tick effect moved to MainLayout
  // This ensures timer continues running when navigating between pages
  // NOTE: tickSessionTime and tickRemainingTime are accessed directly by MainLayout from store

  // Format time as MM:SS or HH:MM:SS
  const formatTime = useCallback((seconds: number): string => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Computed values
  const isRunning = timerState === 'running';
  const formattedSession = formatTime(sessionElapsedTime);
  const formattedRemaining = formatTime(remainingTime);

  return {
    // State
    timerState,
    currentTimerProfile,
    sessionElapsedTime,
    remainingTime,
    isInBreak,
    pomodoroPhase,
    isRunning,

    // Formatted values
    formattedSession,
    formattedRemaining,

    // Actions
    setTimerState,
    setTimerProfile,
    resetSession,
    toggleBreak,

    // Convenience actions for UI
    startTimer: () => setTimerState('running'),
    pauseTimer: () => setTimerState('paused'),
    resetTimer: resetSession,
  };
}

// =============================================================================
// VISUALIZER HOOK
// =============================================================================

/**
 * Hook for audio visualization using AnalyserNode
 */
export function useAudioVisualizer() {
  const isPlaying = usePlayerStore((s) => s.isPlaying);
  const dataArrayRef = useRef<Uint8Array<ArrayBuffer> | null>(null);

  const getFrequencyData = useCallback((): Uint8Array<ArrayBuffer> | null => {
    const engine = getAudioEngine();
    const analyser = engine.getAnalyser();

    if (!analyser || !isPlaying) return null;

    if (!dataArrayRef.current || dataArrayRef.current.length !== analyser.frequencyBinCount) {
      dataArrayRef.current = new Uint8Array(analyser.frequencyBinCount);
    }

    analyser.getByteFrequencyData(dataArrayRef.current);
    return dataArrayRef.current;
  }, [isPlaying]);

  const getTimeDomainData = useCallback((): Uint8Array<ArrayBuffer> | null => {
    const engine = getAudioEngine();
    const analyser = engine.getAnalyser();

    if (!analyser || !isPlaying) return null;

    if (!dataArrayRef.current || dataArrayRef.current.length !== analyser.frequencyBinCount) {
      dataArrayRef.current = new Uint8Array(analyser.frequencyBinCount);
    }

    analyser.getByteTimeDomainData(dataArrayRef.current);
    return dataArrayRef.current;
  }, [isPlaying]);
  
  return {
    getFrequencyData,
    getTimeDomainData,
    isActive: isPlaying,
  };
}

// =============================================================================
// AUTO-INIT HOOK
// =============================================================================

/**
 * Hook that automatically initializes the audio engine on user interaction
 * Useful for handling browser autoplay policies
 */
export function useAutoInit() {
  const { initialize, isInitialized, error } = useAudioEngine();
  const hasTriedRef = useRef(false);
  
  const handleUserInteraction = useCallback(async () => {
    if (!isInitialized && !hasTriedRef.current) {
      hasTriedRef.current = true;
      await initialize();
    }
  }, [isInitialized, initialize]);
  
  useEffect(() => {
    // Add listeners for first user interaction
    const events = ['click', 'touchstart', 'keydown'];
    
    events.forEach((event) => {
      document.addEventListener(event, handleUserInteraction, { once: true });
    });
    
    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleUserInteraction);
      });
    };
  }, [handleUserInteraction]);
  
  return { isInitialized, error };
}

export default useAudioEngine;